import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronSelectorVertical, LogOut01, Settings01, Users01 } from "@untitledui/icons";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from "react-aria-components";
import type { Placement } from "@react-types/overlays";
import { Avatar } from "@/components/base/avatar/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";

// Admin-adapted account card. The console admin is identified only by the
// website-auth email (no org/plan/profile like Shield), so this shows the email
// + a minimal menu (Members, Sign out). Keeps the Shield* export names so the
// vendored sidebar/slim components consume it unchanged.

function initialsFor(email: string | null | undefined) {
  const source = email?.split("@")[0] ?? "";
  if (!source) return "?";
  const parts = source.split(/[.\-_]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function useUserDisplay() {
  const { user } = useAuth();
  const email = user?.email ?? null;
  const fullName = (user?.user_metadata?.full_name as string | undefined)?.trim() || null;
  return {
    name: fullName ?? email?.split("@")[0] ?? "User",
    email,
    initials: initialsFor(fullName ?? email),
  };
}

function AccountMenu({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  return (
    <div className="flex w-56 flex-col rounded-xl bg-primary py-1.5 shadow-lg ring-1 ring-secondary outline-hidden">
      <MenuItem icon={Settings01} label={t("shell.nav.settings")} onClick={() => handleNav("/settings")} />
      <MenuItem icon={Users01} label={t("shell.nav.members")} onClick={() => handleNav("/members")} />
      <div className="my-1 border-t border-secondary" />
      <MenuItem icon={LogOut01} label={t("shell.signOut")} onClick={handleSignOut} />
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick }: { icon: typeof Users01; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-secondary outline-focus-ring transition-colors hover:bg-primary_hover hover:text-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <Icon className="size-4 text-fg-quaternary" />
      {label}
    </button>
  );
}

export function ShieldAccountCard({ popoverPlacement }: { popoverPlacement?: Placement }) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint("lg");
  const { t } = useTranslation();
  const { name, email, initials } = useUserDisplay();

  return (
    <div ref={triggerRef} className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
      <Avatar size="md" initials={initials} alt={name} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-primary">{name}</span>
        {email && <span className="truncate text-xs text-tertiary">{email}</span>}
      </div>
      <AriaDialogTrigger>
        <AriaButton
          aria-label={t("shell.accountMenu")}
          className="absolute top-2 right-2 flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition-colors hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover"
        >
          <ChevronSelectorVertical className="size-4 shrink-0 stroke-[2.25px]" />
        </AriaButton>
        <AriaPopover
          placement={popoverPlacement ?? (isDesktop ? "right bottom" : "top right")}
          triggerRef={triggerRef}
          offset={8}
          className={({ isEntering, isExiting }) =>
            cx("will-change-transform", isEntering && "duration-150 ease-out animate-in fade-in", isExiting && "duration-100 ease-in animate-out fade-out")
          }
        >
          <AriaDialog className="outline-hidden">{({ close }) => <AccountMenu onClose={close} />}</AriaDialog>
        </AriaPopover>
      </AriaDialogTrigger>
    </div>
  );
}

/** Compact avatar-only trigger for the slim/icon-only sidebar. */
export function ShieldAccountAvatar() {
  const isDesktop = useBreakpoint("lg");
  const { t } = useTranslation();
  const { name, initials } = useUserDisplay();

  return (
    <AriaDialogTrigger>
      <AriaButton aria-label={t("shell.accountMenu")} className="group relative inline-flex rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2">
        <Avatar size="md" initials={initials} alt={name} status="online" />
      </AriaButton>
      <AriaPopover
        placement={isDesktop ? "right bottom" : "top right"}
        offset={8}
        crossOffset={6}
        className={({ isEntering, isExiting }) =>
          cx("will-change-transform", isEntering && "duration-150 ease-out animate-in fade-in", isExiting && "duration-100 ease-in animate-out fade-out")
        }
      >
        <AriaDialog className="outline-hidden">{({ close }) => <AccountMenu onClose={close} />}</AriaDialog>
      </AriaPopover>
    </AriaDialogTrigger>
  );
}
